import i18n from '../i18n';
import { formatDate } from './locale';
import { isSupportingCompany } from './userCompany';

export const BILLING_PATH = '/my-company#billing';
export const USERS_PATH = '/users';

export const UPGRADE_CTA =
  'Upgrade your plan in Company settings to unlock this feature.';

export const getActiveCompanyPlan = (user) => user?.activeCompanyPlan || null;

export const isReadOnlyPlan = (user) => Boolean(getActiveCompanyPlan(user)?.readOnly);

export const isCompanyAdmin = (user) => {
  if (!user) {
    return false;
  }
  if (isSupportingCompany(user) || user.role === 'super_admin') {
    return true;
  }
  return (user.active_company_role || user.role) === 'admin';
};

export const getEffectiveCompanyRole = (user) => {
  if (isSupportingCompany(user)) {
    return 'admin';
  }
  if (user?.role === 'super_admin') {
    return 'super_admin';
  }
  return user?.active_company_role || user?.role;
};

export const isCompanyEmployee = (user) =>
  user?.role !== 'super_admin' &&
  !isCompanyAdmin(user) &&
  getEffectiveCompanyRole(user) === 'employee';

export const isCompanyReader = (user) =>
  user?.role !== 'super_admin' &&
  !isCompanyAdmin(user) &&
  getEffectiveCompanyRole(user) === 'reader';

export const canSubmitTaskDraft = (user) =>
  isCompanyAdmin(user) || isCompanyEmployee(user);

export const canParticipateInAssignedTasks = (user) =>
  isCompanyAdmin(user) || isCompanyEmployee(user) || isCompanyReader(user);

export const canClaimOrReturnHandout = (user) =>
  isCompanyAdmin(user) || isCompanyEmployee(user);

export const getReadOnlyPlanBanner = (user) => {
  const plan = getActiveCompanyPlan(user);
  if (!plan?.readOnly || (user?.role === 'super_admin' && !isSupportingCompany(user))) {
    return null;
  }

  if (plan.readOnlyReason === 'over_user_cap') {
    const usersToRemove = Math.max(0, plan.memberCount - plan.maxUsers);
    return {
      reason: 'over_user_cap',
      memberCount: plan.memberCount,
      maxUsers: plan.maxUsers,
      planId: plan.planId,
      usersToRemove
    };
  }

  if (plan.readOnlyReason === 'past_due') {
    return {
      reason: 'past_due'
    };
  }

  return null;
};

const TRIAL_PLAN_LABELS = {
  professional: 'Professional',
  enterprise: 'Enterprise'
};

export const formatTrialEndDate = (iso) => {
  if (!iso) {
    return null;
  }

  return formatDate(iso, i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

export const getTrialPlanBanner = (user) => {
  const plan = getActiveCompanyPlan(user);
  if (!plan?.trialActive || (user?.role === 'super_admin' && !isSupportingCompany(user))) {
    return null;
  }

  const tierLabel = TRIAL_PLAN_LABELS[plan.trialPlan] || plan.trialPlan;
  const endDate = formatTrialEndDate(plan.trialEndsAt);

  return {
    title: i18n.t('trialBannerTitle', { ns: 'company' }),
    message: i18n.t('trialBannerMessage', {
      ns: 'company',
      tier: tierLabel,
      date: endDate
    })
  };
};

export const hasPlanCapability = (user, capability) => {
  const plan = getActiveCompanyPlan(user);
  if (!plan?.capabilities) {
    return false;
  }
  return Boolean(plan.capabilities[capability]);
};

export const canAccessFeature = (user, capability) =>
  (user?.role === 'super_admin' && !isSupportingCompany(user))
  || (hasPlanCapability(user, capability) && !isReadOnlyPlan(user));

export const canInviteUsers = (user) => {
  const plan = getActiveCompanyPlan(user);
  if (!plan || isReadOnlyPlan(user)) {
    return false;
  }
  return plan.seatCount < plan.maxUsers;
};

export const getUserCapLabel = (user, t) => {
  const plan = getActiveCompanyPlan(user);
  if (!plan) {
    return null;
  }
  if (t) {
    return t('common:userCapLabel', { count: plan.seatCount, max: plan.maxUsers });
  }
  return `${plan.seatCount} / ${plan.maxUsers} users`;
};

export const PLAN_FEATURE_LABELS = {
  exportData: 'Data export',
  taskPresets: 'Task presets',
  checklistPresets: 'Checklist presets',
  taskImageUploads: 'Task photo uploads',
  handouts: 'Handouts',
  advancedAnalytics: 'Advanced analytics',
  customIntegrations: 'Custom integrations',
  taskWorkGroups: 'Task work groups'
};
